import { useCallback, useRef } from 'react';

import {
  AUTO_SCROLL_EDGE_ZONE_PX,
  AUTO_SCROLL_SPEED_PX,
  PLANNER_CONTAINER_ID,
  PLANNER_SIDEBAR_WIDTH_PX,
} from '../../constants';

/**
 * Calculates the horizontal scroll amount based on cursor proximity to container edges.
 * Returns 0 when cursor is in the center, negative for left scroll, positive for right.
 * The left edge accounts for the sticky sidebar — auto-scroll triggers when the cursor
 * is near the sidebar's right boundary (the visible timeline edge), not the container's left edge.
 */
const getEdgeScrollAmount = (mouseX: number): number => {
  const container = document.getElementById(PLANNER_CONTAINER_ID);
  if (!container) return 0;

  const rect = container.getBoundingClientRect();
  const timelineLeft = rect.left + PLANNER_SIDEBAR_WIDTH_PX;
  const distFromLeft = mouseX - timelineLeft;
  const distFromRight = rect.right - mouseX;

  if (distFromLeft < AUTO_SCROLL_EDGE_ZONE_PX) {
    const ratio = Math.min(1, 1 - distFromLeft / AUTO_SCROLL_EDGE_ZONE_PX);
    return -Math.round(ratio * AUTO_SCROLL_SPEED_PX);
  }

  if (distFromRight < AUTO_SCROLL_EDGE_ZONE_PX) {
    const ratio = Math.min(1, 1 - distFromRight / AUTO_SCROLL_EDGE_ZONE_PX);
    return Math.round(ratio * AUTO_SCROLL_SPEED_PX);
  }

  return 0;
};

interface UseAutoScrollReturn {
  /** Ref tracking the last known mouse X position — update this in mousemove handlers */
  lastMouseXRef: React.RefObject<number>;
  /** Start the continuous RAF auto-scroll loop. `onScroll` is called on each frame that scrolled. */
  startAutoScroll: (onScroll: (scrollAmount: number) => void) => void;
  /** Stop the auto-scroll loop. */
  stopAutoScroll: () => void;
}

/**
 * Hook that manages a continuous requestAnimationFrame loop for auto-scrolling
 * the planner container when the cursor is near its edges during drag interactions.
 *
 * The consumer is responsible for:
 * - Updating `lastMouseXRef.current` in their mousemove handler
 * - Calling `startAutoScroll` when drag begins and `stopAutoScroll` when it ends
 * - Providing an `onScroll` callback for interaction-specific logic (e.g. syncing phantom position)
 */
export const useAutoScroll = (): UseAutoScrollReturn => {
  const rafRef = useRef<number | null>(null);
  const lastMouseXRef = useRef<number>(0);

  const stopAutoScroll = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(
    (onScroll: (scrollAmount: number) => void) => {
      stopAutoScroll();

      const loop = () => {
        const scrollAmount = getEdgeScrollAmount(lastMouseXRef.current);
        if (scrollAmount !== 0) {
          const container = document.getElementById(PLANNER_CONTAINER_ID);
          if (container) {
            container.scrollLeft += scrollAmount;
            onScroll(scrollAmount);
          }
        }
        rafRef.current = requestAnimationFrame(loop);
      };

      rafRef.current = requestAnimationFrame(loop);
    },
    [stopAutoScroll]
  );

  return { lastMouseXRef, startAutoScroll, stopAutoScroll };
};
